import React, {useMemo, useRef, useState} from "react";
import {pageAuthorizedByGroup} from "../fragments/PageAuthorization";
import {formatDateTimeShort, GlobalState} from "../../global/GlobalState";
import {Link} from "react-router-dom";
import {
	Alert,
	Box,
	Breadcrumbs,
	Button,
	Checkbox,
	Chip,
	FormControlLabel,
	Paper,
	Popover,
	Snackbar,
	Stack,
	TextField,
	Typography
} from "@mui/material";
import {LocalDate} from "@js-joda/core";
import {DatePicker} from "../../components/common/input/DatePicker";
import {copyableColumn, textColumn, tooltipColumn, uuidColumn} from "../../components/common/grid/Columns";
import {PentaSplit} from "../../components/common/PentaSplit";
import {GraphQlDataGrid, GraphQlDataGridHandle} from "../../components/common/grid/GraphQlDataGrid";
import {graphQLServices} from "../../services/GraphQLServices";
import {translateFunction} from "../../localization/TranslationService";
import {CurrentStageDisplay, HeaderSlot} from "../../components/onsite/Footer";
import {
	COMPARATOR_INTAKES_IDS_QUERY,
	COMPARATOR_INTAKES_REPORT_QUERY,
	SEND_COMPARATOR_INTAKES_EMAILS_MUTATION
} from "./Queries";
import {useLazyQuery, useMutation} from "@apollo/client";
import {FilterList, Send} from "@mui/icons-material";

import {GridColDef} from "@mui/x-data-grid";

const translate = translateFunction("SureBasePortalComparatorIntakes");

// The backend pages up to 10.000 rows per request; stay under that when collecting ids to send, and
// batch the send itself so one mutation never carries an unbounded list.
const ID_PAGE_SIZE = 5000;
const SEND_BATCH_SIZE = 5000;

export const ComparatorIntakesReport = pageAuthorizedByGroup(function () {
	return <>
		<HeaderSlot><CurrentStageDisplay/></HeaderSlot>
		<PentaSplit
			top={<Breadcrumbs style={{margin: "5px"}}>
				<Link style={{color: "inherit", textDecoration: "unset"}} to="/reports">{translate("Reports")}</Link>
				<Typography color="text.primary">{translate("Comparator intakes report")}</Typography>
			</Breadcrumbs>}
			center={<Paper style={{
				height: "100%",
			}}>
				<ComparatorIntakesReportContent/>
			</Paper>}
		/>
	</>;
}, ["Admin", "Product"]);

export function ComparatorIntakesReportContent(props: { tenant?: string }) {
	const gridRef = useRef<GraphQlDataGridHandle>(null);
	const [snackbar, setSnackbar] = useState<{open: boolean; message: string; severity: "success" | "error"}>({
		open: false,
		message: "",
		severity: "success"
	});
	const [excludedEmailsText, setExcludedEmailsText] = useState<string>("");
	const [excludedEmails, setExcludedEmails] = useState<string[]>([]);
	const [popoverAnchorEl, setPopoverAnchorEl] = useState<HTMLButtonElement | null>(null);
	const [excludeHealthCurrentOrders, setExcludeHealthCurrentOrders] = useState<boolean>(false);
	// Two pickers rather than a DateRangePicker: the shared js-joda adapter's getDiff hands its raw value
	// to ChronoUnit.between, which throws on a LocalDate for any non-time unit. The repo's own DatePicker
	// already does the LocalDate/LocalDateTime dance internally, which is what keeps it working.
	const [healthOrdersFrom, setHealthOrdersFrom] = useState<LocalDate | null>(null);
	const [healthOrdersUntil, setHealthOrdersUntil] = useState<LocalDate | null>(null);

	const healthOrdersRangeIncomplete = excludeHealthCurrentOrders && (!healthOrdersFrom || !healthOrdersUntil);

	const [fetchIds] = useLazyQuery(COMPARATOR_INTAKES_IDS_QUERY, {
		client: graphQLServices.comparatorIntakesClient,
		fetchPolicy: "network-only"
	});

	const [sendEmails, {loading: sendingEmails}] = useMutation(SEND_COMPARATOR_INTAKES_EMAILS_MUTATION, {
		client: graphQLServices.comparatorIntakesClient
	});

	const handleSendEmails = async () => {
		try {
			// Whatever the grid is currently filtered on is what gets sent, so the operator sees exactly
			// the selection they are about to mail.
			const whereClause = gridRef.current?.graphQlWhereClause();

			const allIds: string[] = [];
			let hasNextPage = true;
			let cursor: string | null = null;

			setSnackbar({
				open: true,
				message: translate("Collecting intakes..."),
				severity: "success"
			});

			while (hasNextPage) {
				const result: any = await fetchIds({
					variables: {
						first: ID_PAGE_SIZE,
						after: cursor,
						where: whereClause
					}
				});

				if (result.data?.comparatorIntakes?.nodes) {
					allIds.push(...result.data.comparatorIntakes.nodes.map((node: any) => node.id));
				}

				hasNextPage = result.data?.comparatorIntakes?.pageInfo?.hasNextPage ?? false;
				cursor = result.data?.comparatorIntakes?.pageInfo?.endCursor ?? null;
			}

			if (allIds.length === 0) {
				setSnackbar({
					open: true,
					message: translate("No intakes found with the current filter"),
					severity: "error"
				});
				return;
			}

			let totalQueued = 0;
			let totalExcluded = 0;
			for (let i = 0; i < allIds.length; i += SEND_BATCH_SIZE) {
				const batch = allIds.slice(i, i + SEND_BATCH_SIZE);

				setSnackbar({
					open: true,
					message: `Queueing batch ${Math.floor(i / SEND_BATCH_SIZE) + 1} of ${Math.ceil(allIds.length / SEND_BATCH_SIZE)} (${totalQueued}/${allIds.length} queued)...`,
					severity: "success"
				});

				const sendResult: any = await sendEmails({
					variables: {
						input: {
							ids: batch,
							excludeHealthCurrentOrders,
							healthOrdersFrom: excludeHealthCurrentOrders ? healthOrdersFrom?.toString() : null,
							healthOrdersUntil: excludeHealthCurrentOrders ? healthOrdersUntil?.toString() : null
						}
					}
				});

				totalQueued += sendResult.data?.sendEmails?.emailsQueued ?? 0;
				totalExcluded += sendResult.data?.sendEmails?.emailsExcluded ?? 0;
			}

			// Queued, not delivered: the SendEventFlows lambda does the sending and stamps the
			// "Last Email Send Date" column, so refreshing the grid shows which ones actually went out.
			setSnackbar({
				open: true,
				message: totalExcluded > 0
					? `Queued ${totalQueued} of ${allIds.length} intakes for sending, ${totalExcluded} skipped with a current health order`
					: `Queued ${totalQueued} of ${allIds.length} intakes for sending`,
				severity: "success"
			});
		} catch (error: any) {
			setSnackbar({
				open: true,
				message: `Error sending emails: ${error.message}`,
				severity: "error"
			});
		}
	};

	// Clears the range on the way out, so re-ticking always starts blank. The shared DatePicker only
	// reports a value when there is one - clearing its box never reaches here - so without this an
	// abandoned range would sit in state, invisible, and still be sent.
	const handleToggleExcludeHealthCurrentOrders = (checked: boolean) => {
		setExcludeHealthCurrentOrders(checked);
		if (!checked) {
			setHealthOrdersFrom(null);
			setHealthOrdersUntil(null);
		}
	};

	const handleCloseSnackbar = () => {
		setSnackbar({...snackbar, open: false});
	};

	const handleOpenPopover = (event: React.MouseEvent<HTMLButtonElement>) => {
		setPopoverAnchorEl(event.currentTarget);
	};

	const handleClosePopover = () => {
		setPopoverAnchorEl(null);
	};

	const handleApplyExcludedEmails = () => {
		const emails = excludedEmailsText
			.split("\n")
			.map(email => email.trim())
			.filter(email => email.length > 0 && email.includes("@"));

		setExcludedEmails(emails);
		setPopoverAnchorEl(null);
		setSnackbar({
			open: true,
			message: `Applied ${emails.length} email exclusions`,
			severity: "success"
		});
	};

	const handleClearExcludedEmails = () => {
		setExcludedEmailsText("");
		setExcludedEmails([]);
		setSnackbar({
			open: true,
			message: translate("Cleared email exclusions"),
			severity: "success"
		});
	};

	const columns: GridColDef[] = [
		{
			field: "id",
			headerName: translate("Intake ID"),
			minWidth: 330,
			renderCell: copyableColumn(tooltipColumn(uuidColumn())),
			flex: 1,
			uuid: true,
		} as any,
		{
			field: "email",
			headerName: translate("Email"),
			minWidth: 330,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "productType",
			headerName: translate("Product Type"),
			minWidth: 200,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "campaignName",
			headerName: translate("Campaign Name"),
			minWidth: 250,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "clientName",
			headerName: translate("Client Name"),
			minWidth: 200,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "comparisonId",
			headerName: translate("Comparison ID"),
			minWidth: 330,
			renderCell: copyableColumn(tooltipColumn(textColumn())),
			flex: 1,
		},
		{
			field: "created",
			headerName: translate("Created date"),
			type: "dateTime",
			minWidth: 250,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
			valueFormatter: (params) => formatDateTimeShort(params.value),
		},
		{
			field: "lastModified",
			headerName: translate("Last Modified Date"),
			type: "dateTime",
			minWidth: 250,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
			valueFormatter: (params) => formatDateTimeShort(params.value),
		},
		{
			field: "lastEmailSend",
			headerName: translate("Last Email Send Date"),
			type: "dateTime",
			minWidth: 250,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
			valueFormatter: (params) => formatDateTimeShort(params.value),
		},
	];

	// Memoized: the grid resets its paging whenever the identity of queryVariables changes, so building
	// this inline would rewind to page one on every render.
	const queryVariables = useMemo(
		() => excludedEmails.length > 0
			? {where: {email: {nin: excludedEmails}}}
			: undefined,
		[excludedEmails]
	);

	return <>
		<GraphQlDataGrid
			ref={gridRef}
			key={`comparator_intakes-${props.tenant ?? "none"}-${excludedEmails.length}`}
			rows={[]}
			query={COMPARATOR_INTAKES_REPORT_QUERY}
			graphFunction={"comparatorIntakes"}
			client={graphQLServices.comparatorIntakesClient}
			rowsPerPageOptions={[50, 500, 1000, 2000, 5000]}
			initialPageSize={50}
			columns={columns}
			queryVariables={queryVariables}
			defaultSortModel={[
				{
					field: "lastModified",
					sort: "desc"
				}
			]}
			initialState={{
				columns: {
					columnVisibilityModel: {
						id: false
					}
				}
			}}
			toolbarElements={
				<Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
					<Button
						variant="outlined"
						size="small"
						onClick={handleOpenPopover}
						startIcon={<FilterList />}
					>
						{translate("Exclude Emails")}
					</Button>
					{excludedEmails.length > 0 && (
						<Chip
							label={`${excludedEmails.length} excluded`}
							color="primary"
							size="small"
							onDelete={handleClearExcludedEmails}
						/>
					)}
					<FormControlLabel
						label={translate("Exclude health current orders")}
						control={<Checkbox
							size="small"
							checked={excludeHealthCurrentOrders}
							onChange={event => handleToggleExcludeHealthCurrentOrders(event.target.checked)}
						/>}
					/>
					{excludeHealthCurrentOrders && <>
						<DatePicker
							label={translate("From")}
							variant={"standard"}
							inputFormat={GlobalState.userPreferences.datePatternOrDefault}
							value={healthOrdersFrom}
							onValueChange={setHealthOrdersFrom}
							textFieldProps={{style: {width: "150px"}}}
						/>
						<DatePicker
							label={translate("Until")}
							variant={"standard"}
							inputFormat={GlobalState.userPreferences.datePatternOrDefault}
							value={healthOrdersUntil}
							onValueChange={setHealthOrdersUntil}
							textFieldProps={{style: {width: "150px"}}}
						/>
					</>}
				</Box>
			}
			customButtons={[
				<Button
					key="send-emails"
					variant="contained"
					color="primary"
					disabled={sendingEmails || healthOrdersRangeIncomplete}
					onClick={handleSendEmails}
					startIcon={<Send />}
				>
					{sendingEmails ? translate("Sending...") : translate("Send Emails")}
				</Button>
			]}
		/>
		<Snackbar
			open={snackbar.open}
			autoHideDuration={6000}
			onClose={handleCloseSnackbar}
			anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
		>
			<Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
				{snackbar.message}
			</Alert>
		</Snackbar>
		<Popover
			open={Boolean(popoverAnchorEl)}
			anchorEl={popoverAnchorEl}
			onClose={handleClosePopover}
			anchorOrigin={{
				vertical: "bottom",
				horizontal: "left",
			}}
			transformOrigin={{
				vertical: "top",
				horizontal: "left",
			}}
		>
			<Box sx={{ p: 3, width: 500 }}>
				<Stack spacing={2}>
					<Typography variant="h6">
						{translate("Exclude Emails")}
					</Typography>
					<Typography variant="body2" color="text.secondary">
						{translate("Enter one email address per line")}
					</Typography>
					<TextField
						label={translate("Email addresses")}
						placeholder="email1@example.com&#10;email2@example.com&#10;email3@example.com"
						multiline
						rows={10}
						value={excludedEmailsText}
						onChange={(e) => setExcludedEmailsText(e.target.value)}
						fullWidth
						variant="outlined"
					/>
					<Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
						<Button
							variant="outlined"
							onClick={handleClosePopover}
						>
							{translate("Cancel")}
						</Button>
						<Button
							variant="contained"
							onClick={handleApplyExcludedEmails}
							disabled={!excludedEmailsText.trim()}
						>
							{translate("Apply")}
						</Button>
					</Box>
				</Stack>
			</Box>
		</Popover>
	</>;
}
